import React from 'react';
import { usePushNotifications } from '../hooks/usePushNotifications';

interface PushNotificationToggleProps {
  tenantId: string;
  customerId?: string;
  isSubscribed: boolean;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const PushNotificationToggle: React.FC<PushNotificationToggleProps> = ({
  tenantId,
  customerId,
  isSubscribed: hasSubscription,
  onSuccess,
  onError,
}) => {
  const {
    permission,
    isSupported,
    isSubscribed: hasPushToken,
    loading,
    fcmToken,
    requestPermission,
    subscribe,
    unsubscribe,
    sendTestNotification,
  } = usePushNotifications(tenantId, customerId);

  // Removed the useEffect that was causing infinite re-renders
  // Error handling is now done directly in the event handlers below

  const handleEnablePush = async () => {
    try {
      await subscribe();
      onSuccess?.();
    } catch (err: any) {
      onError?.(err.message || 'Erreur lors de l\'activation des notifications push');
    }
  };

  const handleDisablePush = async () => {
    try {
      await unsubscribe();
      onSuccess?.();
    } catch (err: any) {
      onError?.(err.message || 'Erreur lors de la désactivation des notifications push');
    }
  };

  const handleTestNotification = async () => {
    try {
      await sendTestNotification();
      onSuccess?.();
    } catch (err: any) {
      const errorMsg = err.message || 'Erreur lors de l\'envoi de la notification test';
      // Si le token n'est pas trouvé côté serveur, proposer de réactiver
      if (errorMsg.includes('Aucun token FCM trouvé')) {
        onError?.('Token non enregistré. Veuillez cliquer sur "Activer" pour enregistrer à nouveau vos notifications.');
      } else {
        onError?.(errorMsg);
      }
    }
  };

  if (!isSupported) {
    return (
      <div className="push-notification-warning">
        <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '0.5rem' }}>
          ⚠️ Les notifications push ne sont pas supportées par votre navigateur
        </p>
      </div>
    );
  }

  if (!hasSubscription) {
    return null; // Ne pas afficher si l'utilisateur n'est pas abonné aux publications
  }

  return (
    <div className="push-notification-toggle">
      <div className="setting-item">
        <div className="setting-info">
          <div className="setting-icon">📱</div>
          <div>
            <div className="setting-label">Notifications push</div>
            <div className="setting-description">
              {permission === 'default' && 'Recevez des notifications sur votre appareil'}
              {permission === 'denied' && 'Notifications bloquées - Vérifiez les paramètres de votre navigateur'}
              {permission === 'granted' && hasPushToken && 'Notifications actives'}
              {permission === 'granted' && !hasPushToken && 'Cliquez pour activer'}
            </div>
          </div>
        </div>

        {permission === 'default' && (
          <button
            onClick={requestPermission}
            disabled={loading}
            style={{
              padding: '0.5rem 1rem',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Autoriser
          </button>
        )}

        {permission === 'granted' && !hasPushToken && (
          <button
            onClick={handleEnablePush}
            disabled={loading}
            style={{
              padding: '0.5rem 1rem',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            {loading ? 'Activation...' : 'Activer'}
          </button>
        )}

        {permission === 'granted' && hasPushToken && (
          <label className="toggle">
            <input
              type="checkbox"
              checked={true}
              onChange={handleDisablePush}
              disabled={loading}
            />
            <span className="toggle-slider"></span>
          </label>
        )}
      </div>

      {permission === 'granted' && hasPushToken && (
        <div style={{ marginTop: '1rem' }}>
          <button
            onClick={handleTestNotification}
            disabled={loading}
            style={{
              padding: '0.625rem 1.25rem',
              background: loading ? '#9ca3af' : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '0.875rem',
              fontWeight: 600,
              width: '100%',
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? 'Envoi en cours...' : '🔔 Envoyer une notification de test'}
          </button>

          {fcmToken && (
            <details style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: '#6b7280' }}>
              <summary style={{ cursor: 'pointer' }}>Informations techniques</summary>
              <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#f3f4f6', borderRadius: '4px', wordBreak: 'break-all' }}>
                <strong>Token FCM:</strong><br />
                {fcmToken.substring(0, 50)}...
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default PushNotificationToggle;
